import DOMPurify from 'dompurify';

/**
 * Gen3 frontend equivalent of Gen2's ConnectPortal.Helpers.SecurityValidationHelper.
 *
 * The primary XSS defence in Gen3 is React's automatic escaping — raw HTML rendering
 * via dangerouslySetInnerHTML is the exception, not the norm. These utilities cover
 * that exception and any other boundary where untrusted text reaches the DOM.
 *
 * Backend counterpart: PortalApi.Infrastructure.Helpers.SecurityValidationHelper
 * Full reference:      docs/security-validation.md
 */

// ── HTML sanitization ─────────────────────────────────────────────────────────

export type SafeHtmlPolicy = 'default' | 'report';

/**
 * Sanitizes an HTML string with DOMPurify before it is passed to
 * dangerouslySetInnerHTML. Strips all script tags, event handlers, and
 * javascript: URIs while preserving safe markup (bold, italic, links, etc.).
 *
 * Use policy `'report'` for SSRS / clinical-summary HTML that relies on
 * `<style>` blocks and inline `style` for layout and image sizing.
 *
 * Gen2 equivalent: GetNoScriptMessage() / GetSafeHtmlFragment()
 *
 * @example
 * <SafeHtml html={htmlFromApi} />
 * <SafeHtml html={clinicalSummaryHtml} policy="report" isolate />
 */
export function getSafeHtml(html: string, policy: SafeHtmlPolicy = 'default'): string {
  if (!html) return '';

  const forbidTags = ['script', 'iframe', 'object', 'embed', 'form'];
  const forbidAttr = [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
    'onkeyup', 'onkeydown', 'onchange', 'onsubmit',
  ];

  if (policy === 'default') {
    forbidTags.push('style');
    forbidAttr.push('style');
  }

  const config = {
    USE_PROFILES: { html: true },
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: forbidAttr,
  };

  if (policy === 'report') {
    // SSRS puts styles in <head>, which fragment sanitization discards.
    // Sanitize the complete document first, then serialize only its styles
    // and body content so SafeHtml can render it inside a normal container.
    const report = DOMPurify.sanitize(html, {
      ...config,
      WHOLE_DOCUMENT: true,
      RETURN_DOM_FRAGMENT: true,
    });
    const styles = Array.from(report.querySelectorAll('head style'), (style) => style.outerHTML).join('');
    return styles + (report.querySelector('body')?.innerHTML ?? '');
  }

  return DOMPurify.sanitize(html, config);
}

// ── Plain-text XSS strip ──────────────────────────────────────────────────────

// Matches HTML tags and javascript: URI schemes — neither belongs in plain text.
const PLAIN_TEXT_XSS = /<[^>]*>|javascript\s*:/gi;

// Matches inline event handler attributes and alert() calls surviving tag removal.
const INLINE_EVENT = /on\w+\s*=\s*(?:alert|confirm|prompt)\s*\(/gi;

/**
 * Strips HTML tags, javascript: URI schemes, and inline event handlers from a
 * plain-text string. Use for any field that should never contain markup
 * (patient names, addresses, phone numbers, free-text search inputs).
 *
 * Gen2 equivalent: GetSafeTextXSS()
 *
 * @example
 * const safeSearch = getSafeText(userInput);
 */
export function getSafeText(text: string): string {
  if (!text) return '';
  return text
    .replace(PLAIN_TEXT_XSS, '')
    .replace(INLINE_EVENT, '');
}

// ── Open-redirect guard ────────────────────────────────────────────────────────

/**
 * Returns true when a URL is safe to use as a local redirect target.
 * Accepts relative paths (/exams, /dashboard) and rejects absolute URLs,
 * protocol-relative URLs (//evil.com), and javascript: URIs.
 *
 * Gen2 equivalent: FindURLLocal()
 *
 * The post-auth redirect in authRedirect.ts already applies this logic;
 * use this function in any other place where a caller-supplied URL is
 * passed to navigate() or window.location.
 *
 * @example
 * if (isLocalUrl(redirectParam)) navigate(redirectParam);
 */
export function isLocalUrl(url: string): boolean {
  if (!url) return false;
  // Relative path — safe
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  return false;
}

// ── Form input sanitization ────────────────────────────────────────────────────

/**
 * Sanitizes a user-supplied form field value before it is displayed back to the
 * user in a non-MUI context (e.g. a toast message echoing user input, or a
 * confirmation screen showing unescaped text). MUI TextField itself escapes
 * output; use this only when rendering the value outside of a controlled input.
 *
 * Applies getSafeText() under the hood.
 *
 * @example
 * const message = t('common.confirmName', { name: sanitizeFormInput(formValue) });
 */
export function sanitizeFormInput(value: string): string {
  return getSafeText(value.trim());
}
